import { yupResolver } from '@hookform/resolvers/yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  HStack,
  SimpleGrid,
  Input,
  Textarea,
  CurrencyInput,
} from '@wegroup/design-system';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { CompanyInvestment } from '../../../../types/CompanyScanTypes';

interface Props {
  companyInvestment?: CompanyInvestment;
  isOpen: boolean;
  onClose: () => void;
  addCompanyInvestment: (companyInvestment: CompanyInvestment) => void;
  editCompanyInvestment: (companyInvestment: CompanyInvestment) => void;
}

const companyInvestmentValidationSchema = yup.object().shape({
  type: yup.string().required('Required'),
  duration: yup.number().typeError('Required'),
  amount: yup.number().typeError('Required'),
  purpose: yup.string(),
});

const NewCompanyInvestmentModal: React.FC<React.PropsWithChildren<Props>> = ({
  companyInvestment,
  addCompanyInvestment,
  editCompanyInvestment,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  const modalGoal = companyInvestment ? 'EDIT' : 'ADD';

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<CompanyInvestment>({
    resolver: yupResolver(companyInvestmentValidationSchema),
    defaultValues: {
      type: companyInvestment?.type,
      duration: companyInvestment?.duration,
      amount: companyInvestment?.amount,
      purpose: companyInvestment?.purpose,
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  const handleOnSubmit = handleSubmit(
    (newCompanyInvestment: CompanyInvestment) => {
      if (companyInvestment) editCompanyInvestment(newCompanyInvestment);
      else addCompanyInvestment(newCompanyInvestment);
      handleOnClose();
    },
  );

  return (
    <Modal isOpen={isOpen} onClose={handleOnClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(
            `FLOWS_COMPANY-SCAN_COMPANY-INVESTMENT-INPUT_${modalGoal}-INVESTMENT`,
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody as={SimpleGrid} columns={2} gap="4">
          <FormControl isInvalid={!!errors.type} gridColumn="span 2">
            <FormLabel>{t('FLOWS_COMPANY-SCAN_LOANS-INPUT_TYPE')}</FormLabel>
            <Input {...register('type')} />
            {errors.type?.message && (
              <FormErrorMessage>{t(errors.type.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.duration}>
            <FormLabel>
              {t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_DURATION')}
            </FormLabel>
            <Input
              {...register('duration')}
              placeholder={t('MONTHS')}
              type="number"
            />
            {errors.duration?.message && (
              <FormErrorMessage>{t(errors.duration.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.amount}>
            <FormLabel>
              {t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_AMOUNT')}
            </FormLabel>
            <Controller
              name="amount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CurrencyInput
                  onChange={({ value }) => onChange(value)}
                  value={value}
                />
              )}
            />
            {errors.amount?.message && (
              <FormErrorMessage>{t(errors.amount.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.purpose} gridColumn="span 2">
            <FormLabel>
              {t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_PURPOSE')}
            </FormLabel>

            <Textarea {...register('purpose')} />
            {errors.purpose?.message && (
              <FormErrorMessage>{t(errors.purpose.message)}</FormErrorMessage>
            )}
          </FormControl>
        </ModalBody>

        <ModalFooter as={HStack} justify="flex-end" spacing="4">
          <Button variant="ghost" mr={3} onClick={handleOnClose}>
            {t('CLOSE')}
          </Button>
          <Button onClick={handleOnSubmit}>
            {modalGoal ? t('SAVE') : t('ADD')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewCompanyInvestmentModal;
